import {
  IThreeLayerID,
  IR2Paths,
  IEntityBase,
  IMetadata
} from '../types/base';
import { IOwnership } from '../types/ownership';
import { IVisibilityChild } from '../types/visibility';
import { IFork } from '../types/fork';
import { IVersionContainer } from '../types/version';
import { ITreeStructure } from '../types/tree';
import { ITrigger } from './ITrigger';
import { createMergeToDefaultData, mergeDataWith } from '../utils/mergeToDefaultData';
import { ulid } from 'ulid';
import { Log } from '@fanfanlo';

const fileLog = new Log(false, 'ITriggerGroup_f')
fileLog.pause = true;
fileLog.childrenPaused = true;
// === 类型常量 ===
export const triggerGroupType = 'triggerGroup'

/**
 * 触发器组接口
 * 对应SQL表：project_trigger_group
 * 对应Go结构：ProjectTriggerGroup
 *
 * list字段存储的是子trigger的ULID列表
 * children字段存储完整的子trigger对象列表（运行时填充）
 */
export interface ITriggerGroup extends
  IThreeLayerID,
  IEntityBase,
  IR2Paths,
  IOwnership,
  IVisibilityChild,
  IFork,
  IVersionContainer,
  ITreeStructure<ITrigger>,
  IMetadata
{
  // list字段继承自ITreeStructure，存储子trigger的ULID
  // children字段继承自ITreeStructure<ITrigger>，存储完整对象
}

// === TriggerGroup Utils ===
function createDefaultTriggerGroup(): ITriggerGroup {
  return {
    ulid: ulid(),
    uuid: '',
    list: [],
    children: [],
    checks: [],
    isSequential: true,
    createdAt: Date.now(),
    updatedAt: Date.now()
  } as ITriggerGroup
}

export interface ITriggerGroupUtils {
  type: string
  createDefaultData: () => ITriggerGroup
  mergeDataToDefaultData: (data: Partial<ITriggerGroup>) => ITriggerGroup
}

export const triggerGroupUtils: ITriggerGroupUtils = {
  type: triggerGroupType,
  createDefaultData: createDefaultTriggerGroup,
  mergeDataToDefaultData: createMergeToDefaultData(createDefaultTriggerGroup)
}

// === Trigger Registry ===
export interface ITriggerUtils<T extends ITrigger> {
  type: string
  init: () => void
  mergeDataToDefaultData: (data: Partial<T>) => T
  createDefaultData: () => T
  create: (triggerGroup: ITriggerGroup, data?: Partial<T>) => void
  update: (trigger: T, data?: Partial<T>) => void
  UI: (data: T) => React.ReactNode
  onClickChange?: (data: { query: any }, ...rest: any[]) => void
  loadI18nTexts?: (data: T) => Promise<void>
}

export interface ITriggerUtilsMap {
  [key: string]: ITriggerUtils<any>
}

export const TriggerUtilsMap: ITriggerUtilsMap = {}

export function registerTriggerUtils(key: string, utils: ITriggerUtils<any>) {
  fileLog.log('[registerTriggerUtils] key:', key)
  if (key in TriggerUtilsMap) {
    throw new Error(`TriggerUtilsMap already has key: ${key}`)
  }
  TriggerUtilsMap[key] = utils
  fileLog.log('[registerTriggerUtils] 注册成功, 当前所有keys:', Object.keys(TriggerUtilsMap))
}

export function getTriggerUtils<T extends ITrigger>(key: string): ITriggerUtils<T> {
  fileLog.log('[getTriggerUtils] key:', key)
  const utils = TriggerUtilsMap[key]
  if (!utils) {
    fileLog.log('[getTriggerUtils] 未找到, 可用的keys:', Object.keys(TriggerUtilsMap))
  } else {
    fileLog.log('[getTriggerUtils] 找到')
  }
  return utils
}

export function createTriggerUtils<T extends ITrigger>(
  type: string,
  init: () => void,
  createDefaultData: () => T,
  UI: (data: T) => React.ReactNode
): ITriggerUtils<T> {
  fileLog.log('[createTriggerUtils] type:', type)
  const res: ITriggerUtils<T> = {
    type,
    mergeDataToDefaultData: createMergeToDefaultData(createDefaultData),
    createDefaultData,
    create: (triggerGroup: ITriggerGroup, data?: Partial<T>) => {
      fileLog.log('[createTriggerUtils.create] triggerGroup.ulid:', triggerGroup.ulid)
      const trigger = createDefaultData()
      fileLog.log('[createTriggerUtils.create] trigger.ulid:', trigger.ulid)
      if (data) {
        fileLog.log('[createTriggerUtils.create] merging data')
        Object.assign(trigger, data)
      }
      // 修改：只维护children（web端主要使用），list由后端维护
      if (!triggerGroup.children) {
        fileLog.log('[createTriggerUtils.create] children未初始化，初始化为[]')
        triggerGroup.children = []
      }
      triggerGroup.children.push(trigger)
      fileLog.log('[createTriggerUtils.create] pushed to children, length:', triggerGroup.children.length)
    },
    update: (trigger: T, data?: Partial<T>) => {
      fileLog.log('[createTriggerUtils.update] trigger.ulid:', trigger.ulid)
      mergeDataWith(trigger, data)
    },
    UI,
    init,
  }
  registerTriggerUtils(type, res)
  fileLog.log('[createTriggerUtils] 注册完成 type:', type)
  return res
}
